import _ from 'lodash'
import { withDependencies, named, optional } from '@wix/thunderbolt-ioc'
import type {
	TpaHandlerProvider,
	Experiments } from '@wix/thunderbolt-symbols'
import {
	PageFeatureConfigSymbol,
	MasterPageFeatureConfigSymbol,
	SiteFeatureConfigSymbol,
	ExperimentsSymbol
} from '@wix/thunderbolt-symbols'
import { createLinkUtils } from '@wix/thunderbolt-commons'
import type { TpaPageConfig, TpaMasterPageConfig } from '../types'
import type { IRoutingLinkUtilsAPI } from 'feature-router'
import { RoutingLinkUtilsAPISymbol } from 'feature-router'
import type { ILightboxUtils } from 'feature-lightbox'
import { LightboxUtilsSymbol } from 'feature-lightbox'
import { name } from '../symbols'
import type { TpaPageLinkData } from '@wix/thunderbolt-becky-types'
import type { TpaCommonsSiteConfig } from 'feature-tpa-commons'
import { name as tpaCommonsName } from 'feature-tpa-commons'
import type { IMultilingualLinkUtilsAPI } from 'feature-multilingual'
import { MultilingualLinkUtilsAPISymbol } from 'feature-multilingual'
import type { ICustomUrlMapper } from 'feature-custom-url-mapper'
import { CustomUrlMapperSymbol } from 'feature-custom-url-mapper'

export type MessageData = { sectionId: string; state: string }
export type HandlerResponse =
	| { url: string }
	| {
		error: {
			message: string
		}
	}

export const GetStateUrlHandler = withDependencies(
	[
		named(SiteFeatureConfigSymbol, tpaCommonsName),
		named(PageFeatureConfigSymbol, name),
		named(MasterPageFeatureConfigSymbol, name),
		RoutingLinkUtilsAPISymbol,
		ExperimentsSymbol,
		optional(LightboxUtilsSymbol),
		optional(MultilingualLinkUtilsAPISymbol),
		optional(CustomUrlMapperSymbol),
	],
	(
		tpaSiteConfig: TpaCommonsSiteConfig,
		tpaPageConfig: TpaPageConfig,
		tpaMasterPageConfig: TpaMasterPageConfig,
		routingLinkUtilsAPI: IRoutingLinkUtilsAPI,
		experiments: Experiments,
		lightboxUtils: ILightboxUtils,
		multilingualLinkUtilsAPI?: IMultilingualLinkUtilsAPI,
		customUrlMapper?: ICustomUrlMapper
	): TpaHandlerProvider => ({
		getTpaHandlers() {
			return {
				getStateUrl(compId, msgData: MessageData, { originCompId }): HandlerResponse {
					const {
						metaSiteId,
						userFileDomainUrl,
						routersConfig,
						appsClientSpecMapData,
						externalBaseUrl,
						isMobileView,
						isPremiumDomain,
					} = tpaSiteConfig

					const linkUtils = createLinkUtils({
						routingInfo: routingLinkUtilsAPI.getLinkUtilsRoutingInfo(),
						metaSiteId,
						userFileDomainUrl,
						routersConfig,
						popupPages: lightboxUtils?.getLightboxPages(),
						multilingualInfo: multilingualLinkUtilsAPI?.getMultilingualInfo(),
						isMobileView,
						isPremiumDomain,
						experiments,
						getIsUrlCustomized: customUrlMapper?.getIsUrlCustomized,
					})

					const appDefIdToAppPagesIds = _(tpaMasterPageConfig.pagesData)
						.groupBy('appDefinitionId')
						.mapValues((pages) => pages.map((page) => page.id))
						.value()
					const pagesDataEntries = Object.entries(tpaMasterPageConfig.pagesData)
					const sectionIdToPageId = pagesDataEntries
						.map(([pageId, pageData]) => ({ [pageData.tpaPageId]: pageId }))
						.reduce(_.assign)

					const getAppDataByAppDefId = (_compId: string) => {
						const appDefId = tpaPageConfig.widgets[_compId].appDefinitionId
						return {
							appData: appsClientSpecMapData[appDefId],
							appPages: appDefIdToAppPagesIds[appDefId],
						}
					}

					const { state, sectionId } = msgData
					const { appData, appPages } = getAppDataByAppDefId(originCompId)

					if (!appPages || appPages.length === 0) {
						return {
							error: {
								message: `Page with app "${appData.appDefinitionName}" was not found.`,
							},
						}
					}
					const sectionPageId = sectionIdToPageId[sectionId]
					const pageId = sectionPageId || appPages[0]
					const linkData = {
						type: 'TpaPageLink',
						pageId,
						path: state,
					} as TpaPageLinkData
					const linkUrl = linkUtils.getLinkUrlFromDataItem(linkData)

					return { url: `${externalBaseUrl}${linkUrl}` }
				},
			}
		},
	})
)
